import { apiClient } from "./client";

export type Role = "admin" | "infection_control" | "clinician" | "auditor";
export type WorkflowState =
  | "open"
  | "reviewing"
  | "intervening"
  | "observing"
  | "closed"
  | "cancelled";
export type RiskLevel = "low" | "medium" | "high" | "critical";

export interface CurrentUser {
  id: string;
  username: string;
  display_name: string;
  role: Role;
  is_active: boolean;
}

export interface PatientSummary {
  id: string;
  external_id: string;
  birth_year: number | null;
  ward: string | null;
  bed: string | null;
  created_at: string;
}

export interface CatheterRecord {
  id: string;
  patient_id: string;
  inserted_at: string;
  removed_at: string | null;
  indication: string;
  insertion_site: string | null;
  active: boolean;
  daily_reviewed_at: string | null;
  removal_recommended: boolean;
  review_note: string | null;
}

export interface RiskAssessmentRecord {
  id: string;
  patient_id: string;
  catheter_id: string | null;
  assessed_at: string;
  score: number;
  level: RiskLevel;
  factors: Record<string, unknown>;
  model_version: string | null;
}

export interface WorkflowSummary {
  id: string;
  patient_id: string;
  assessment_id: string | null;
  state: WorkflowState;
  version: number;
  closed_at: string | null;
  close_reason: string | null;
}

export interface PatientDetail extends PatientSummary {
  catheters: CatheterRecord[];
  assessments: RiskAssessmentRecord[];
  workflows: WorkflowSummary[];
}

export interface ClinicalInput {
  age?: number | null;
  admission_time?: string | null;
  catheter_start?: string | null;
  catheter_days?: number | null;
  catheter_count?: number | null;
  diagnosis_text?: string;
  surgery_names?: string;
  surgery_durations?: string;
  abx_used?: boolean | null;
  abx_combined?: boolean;
  abx_names?: string;
  albumin?: number | null;
  creatinine?: number | null;
  ddimer?: number | null;
  blood_wbc?: number | null;
  neutrophil_pct?: number | null;
}

export interface PredictionResult {
  score: number;
  tier: string;
  tier_label: string;
  level: RiskLevel;
  risk_index: number;
  calibrated_rate: number;
  calibrated_rate_text: string;
  action: string;
  factors: string[];
  missing_fields: string[];
  model_version: string;
  assessment_id: string | null;
  workflow_id: string | null;
}

export interface RiskQueueRow {
  patient_id: string;
  external_id: string;
  ward: string | null;
  bed: string | null;
  assessment_id: string;
  assessed_at: string;
  score: number;
  level: RiskLevel;
  risk_index: number;
  tier_label: string;
  calibrated_rate_text: string;
  factors: string[];
  action: string;
  catheter_days: number | null;
  workflow_id: string | null;
  workflow_state: WorkflowState | null;
  overdue: boolean;
}

export interface DailySummary {
  generated_at: string;
  immediate_count: number;
  today_count: number;
  open_workflows: number;
  overdue_workflows: number;
  active_catheters: number;
  catheters_over_14_days: number;
  pending_reviews: number;
  confirmed_cauti: number;
}

export interface WorkflowAction {
  id: string;
  workflow_id: string;
  kind: string;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface NotificationRecord {
  id: string;
  workflow_id: string;
  channel: string;
  recipient_ref: string;
  status: string;
  delivery_metadata: Record<string, unknown>;
  created_at: string;
}

export interface InfectionReviewRecord {
  id: string;
  patient_id: string;
  workflow_id: string;
  verdict: string;
  evidence: Record<string, unknown>;
  reviewed_at: string | null;
  created_at: string;
}

export interface PdcaTaskRecord {
  id: string;
  title: string;
  owner_ref: string;
  phase: string;
  ward: string | null;
  target: string | null;
  progress: number;
  status: string;
  due_at: string | null;
  version: number;
  created_at: string;
}

export interface ImportRow {
  row_number: number;
  external_id: string | null;
  ward: string | null;
  bed: string | null;
  tier_label: string | null;
  risk_index: number | null;
  score: number | null;
  error: string | null;
}

export interface ImportResult {
  total_rows: number;
  scored_rows: number;
  error_rows: number;
  committed: boolean;
  created_patients: number;
  created_assessments: number;
  created_workflows: number;
  missing_optional_columns: string[];
  rows: ImportRow[];
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export const clinicalApi = {
  login(username: string, password: string): Promise<CurrentUser> {
    return apiClient.request("/api/v1/auth/login", {
      method: "POST",
      requireAuth: false,
      body: JSON.stringify({ username, password }),
    });
  },
  me(): Promise<CurrentUser> {
    return apiClient.request("/api/v1/auth/me");
  },
  logout(): Promise<void> {
    return apiClient.request("/api/v1/auth/logout", { method: "POST" });
  },
  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return apiClient.request("/api/v1/auth/password", {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },
  users(): Promise<CurrentUser[]> {
    return apiClient.request("/api/v1/users");
  },
  createUser(input: {
    username: string;
    display_name: string;
    role: Role;
    password: string;
  }): Promise<CurrentUser> {
    return apiClient.request("/api/v1/users", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  setUserActive(userId: string, isActive: boolean): Promise<CurrentUser> {
    return apiClient.request(`/api/v1/users/${userId}/status`, {
      method: "POST",
      body: JSON.stringify({ is_active: isActive }),
    });
  },
  resetUserPassword(userId: string, newPassword: string): Promise<void> {
    return apiClient.request(`/api/v1/users/${userId}/password`, {
      method: "POST",
      body: JSON.stringify({ new_password: newPassword }),
    });
  },
  dailySummary(): Promise<DailySummary> {
    return apiClient.request("/api/v1/reports/daily-summary");
  },
  riskQueue(ward?: string, actionableOnly = false): Promise<RiskQueueRow[]> {
    return apiClient.request(
      `/api/v1/risk-queue/today${query({ ward, actionable_only: actionableOnly })}`,
    );
  },
  patients(ward?: string, q?: string): Promise<PatientSummary[]> {
    return apiClient.request(`/api/v1/patients${query({ ward, q })}`);
  },
  upsertPatient(input: {
    source_identifier: string;
    birth_year?: number | null;
    ward?: string | null;
    bed?: string | null;
  }): Promise<PatientSummary> {
    return apiClient.request("/api/v1/patients/upsert", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  patientDetail(patientId: string): Promise<PatientDetail> {
    return apiClient.request(`/api/v1/patients/${patientId}/detail`);
  },
  predict(input: ClinicalInput, patientId?: string, persist = false): Promise<PredictionResult> {
    return apiClient.request("/api/v1/predictions", {
      method: "POST",
      body: JSON.stringify({ ...input, patient_id: patientId ?? null, persist }),
    });
  },
  workflows(state?: WorkflowState, patientId?: string): Promise<WorkflowSummary[]> {
    return apiClient.request(`/api/v1/workflows${query({ state, patient_id: patientId })}`);
  },
  transitionWorkflow(
    id: string,
    targetState: WorkflowState,
    expectedVersion: number,
    reason?: string,
  ): Promise<WorkflowSummary> {
    return apiClient.request(`/api/v1/workflows/${id}/transitions`, {
      method: "POST",
      body: JSON.stringify({
        target_state: targetState,
        expected_version: expectedVersion,
        reason: reason ?? null,
      }),
    });
  },
  workflowActions(id: string): Promise<WorkflowAction[]> {
    return apiClient.request(`/api/v1/workflows/${id}/actions`);
  },
  addWorkflowAction(id: string, kind: string, detail: Record<string, unknown>): Promise<WorkflowAction> {
    return apiClient.request(`/api/v1/workflows/${id}/actions`, {
      method: "POST",
      body: JSON.stringify({ kind, detail }),
    });
  },
  notify(
    workflowId: string,
    recipient: string,
    message: string,
    channel = "ward_station",
  ): Promise<NotificationRecord> {
    return apiClient.request("/api/v1/notifications", {
      method: "POST",
      body: JSON.stringify({
        workflow_id: workflowId,
        recipient_ref: recipient,
        message,
        channel,
      }),
    });
  },
  notifications(workflowId?: string): Promise<NotificationRecord[]> {
    return apiClient.request(`/api/v1/notifications${query({ workflow_id: workflowId })}`);
  },
  catheters(patientId?: string, active?: boolean): Promise<CatheterRecord[]> {
    return apiClient.request(`/api/v1/catheters${query({ patient_id: patientId, active })}`);
  },
  reviewCatheter(id: string, removalRecommended: boolean, note?: string): Promise<CatheterRecord> {
    return apiClient.request(`/api/v1/catheters/${id}/review`, {
      method: "POST",
      body: JSON.stringify({ removal_recommended: removalRecommended, review_note: note ?? null }),
    });
  },
  removeCatheter(id: string, removedAt: string, note?: string): Promise<CatheterRecord> {
    return apiClient.request(`/api/v1/catheters/${id}/remove`, {
      method: "POST",
      body: JSON.stringify({ removed_at: removedAt, review_note: note ?? null }),
    });
  },
  infectionReviews(verdict?: string): Promise<InfectionReviewRecord[]> {
    return apiClient.request(`/api/v1/infection-reviews${query({ verdict })}`);
  },
  createInfectionReview(workflowId: string, patientId: string): Promise<InfectionReviewRecord> {
    return apiClient.request("/api/v1/infection-reviews", {
      method: "POST",
      body: JSON.stringify({ workflow_id: workflowId, patient_id: patientId }),
    });
  },
  decideInfectionReview(
    id: string,
    verdict: string,
    evidence: Record<string, unknown> = {},
  ): Promise<InfectionReviewRecord> {
    return apiClient.request(`/api/v1/infection-reviews/${id}/decision`, {
      method: "POST",
      body: JSON.stringify({ verdict, evidence }),
    });
  },
  pdcaTasks(status?: string): Promise<PdcaTaskRecord[]> {
    return apiClient.request(`/api/v1/pdca/tasks${query({ status })}`);
  },
  createPdcaTask(input: {
    title: string;
    owner_ref: string;
    ward?: string;
    target?: string;
    due_at?: string;
  }): Promise<PdcaTaskRecord> {
    return apiClient.request("/api/v1/pdca/tasks", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updatePdcaTask(
    id: string,
    expectedVersion: number,
    patch: { phase?: string; progress?: number; status?: string },
  ): Promise<PdcaTaskRecord> {
    return apiClient.request(`/api/v1/pdca/tasks/${id}`, {
      method: "POST",
      body: JSON.stringify({ ...patch, expected_version: expectedVersion }),
    });
  },
  importPredict(contentBase64: string, filename: string, commit: boolean): Promise<ImportResult> {
    return apiClient.request("/api/v1/imports/predict", {
      method: "POST",
      body: JSON.stringify({ content_base64: contentBase64, filename, commit }),
    });
  },
};
